import {useMemo} from 'react';
import {FormatCurrencyOptions, useIntl} from '@sail/intl';
import {useCountryCode} from 'mkt-ssr/lib/globalization/context';
import {Countries} from 'mkt-ssr/generated/globalization/countries';
import {CountryCode, DefaultCurrency} from 'mkt-ssr/lib/globalization/types';

/**
 * Approximate exchange rates for major currencies (for demo purposes)
 * Base: 1 USD
 * Values chosen to produce round numbers when converting typical amounts
 */
const EXCHANGE_RATES: Record<DefaultCurrency, number> = {
  usd: 1.0,
  eur: 0.9, // Euro - slightly less than dollar
  gbp: 0.8, // British Pound - ~80% of dollar
  jpy: 150.0, // Japanese Yen - major round number
  cad: 1.35, // Canadian Dollar - ~35% more than USD
  aud: 1.5, // Australian Dollar - 50% more than USD
  chf: 0.9, // Swiss Franc - similar to EUR
  hkd: 8.0, // Hong Kong Dollar - round number
  inr: 80.0, // Indian Rupee - round number
  mxn: 17.0, // Mexican Peso
  brl: 5.0, // Brazilian Real - simple 5x
  sek: 10.0, // Swedish Krona - round 10x
  nok: 10.0, // Norwegian Krone - round 10x
  dkk: 7.0, // Danish Krone
  sgd: 1.35, // Singapore Dollar - similar to CAD
  nzd: 1.6, // New Zealand Dollar
  pln: 4.0, // Polish Zloty - round number
  thb: 35.0, // Thai Baht
  myr: 4.5, // Malaysian Ringgit
  aed: 3.7, // UAE Dirham
  czk: 23.0, // Czech Koruna
  huf: 360.0, // Hungarian Forint
  ron: 4.5, // Romanian Leu
};

/**
 * Currencies that don't use minor units (cents, pence, etc.)
 * Amounts are already in the major unit
 */
const ZERO_DECIMAL_CURRENCIES = new Set<DefaultCurrency>(['jpy']);

/**
 * Get the currency code for a given country code
 */
function getCurrencyForCountry(countryCode: CountryCode): DefaultCurrency {
  const country = Countries[countryCode as keyof typeof Countries];
  return country?.defaultCurrency || 'usd';
}

interface PrecisionPattern {
  cents: number;
  majorAmount: number;
  hasMarketingEnding: boolean;
  marketingPattern: '.99' | '.95' | '.90' | '.50' | '.00' | 'other';
  precision:
    | 'specific'
    | 'half-dollar'
    | 'dollar'
    | 'ten'
    | 'hundred'
    | 'thousand'
    | 'ten-thousand';
  appearsIntentionallyRound: boolean;
  appearsCalculated: boolean;
  magnitude: 'micro' | 'small' | 'medium' | 'large' | 'xlarge';
}

/**
 * Detect the precision and design intent of the original USD amount
 */
function detectPrecisionPattern(amountInCents: number): PrecisionPattern {
  const cents = amountInCents % 100;
  const majorAmount = Math.floor(amountInCents / 100);

  let marketingPattern: '.99' | '.95' | '.90' | '.50' | '.00' | 'other';
  if (cents === 99) marketingPattern = '.99';
  else if (cents === 95) marketingPattern = '.95';
  else if (cents === 90) marketingPattern = '.90';
  else if (cents === 50) marketingPattern = '.50';
  else if (cents === 0) marketingPattern = '.00';
  else marketingPattern = 'other';

  const hasMarketingEnding = cents === 99 || cents === 95 || cents === 90;

  let precision:
    | 'specific'
    | 'half-dollar'
    | 'dollar'
    | 'ten'
    | 'hundred'
    | 'thousand'
    | 'ten-thousand';

  if (
    cents !== 0 &&
    cents !== 50 &&
    cents !== 99 &&
    cents !== 95 &&
    cents !== 90
  ) {
    precision = 'specific';
  } else if (cents === 50) {
    precision = 'half-dollar';
  } else if (cents === 0) {
    if (majorAmount % 10000 === 0 && majorAmount >= 10000) {
      precision = 'ten-thousand';
    } else if (majorAmount % 1000 === 0 && majorAmount >= 1000) {
      precision = 'thousand';
    } else if (majorAmount % 100 === 0 && majorAmount >= 100) {
      precision = 'hundred';
    } else if (majorAmount % 10 === 0 && majorAmount >= 10) {
      precision = 'ten';
    } else {
      precision = 'dollar';
    }
  } else if (hasMarketingEnding) {
    precision = 'dollar';
  } else {
    precision = 'specific';
  }

  const appearsCalculated =
    precision === 'specific' ||
    (majorAmount > 1000 && cents !== 0 && !hasMarketingEnding);

  const appearsIntentionallyRound =
    precision === 'thousand' ||
    precision === 'hundred' ||
    precision === 'ten-thousand' ||
    (precision === 'dollar' && majorAmount % 5 === 0) ||
    hasMarketingEnding;

  let magnitude: 'micro' | 'small' | 'medium' | 'large' | 'xlarge';
  if (majorAmount === 0) magnitude = 'micro';
  else if (majorAmount < 10) magnitude = 'small';
  else if (majorAmount < 100) magnitude = 'medium';
  else if (majorAmount < 1000) magnitude = 'large';
  else magnitude = 'xlarge';

  return {
    cents,
    majorAmount,
    hasMarketingEnding,
    marketingPattern,
    precision,
    appearsIntentionallyRound,
    appearsCalculated,
    magnitude,
  };
}

/**
 * Convert amount while preserving the detected precision pattern
 */
function convertWithPrecisionIntent(
  usdAmountInCents: number,
  exchangeRate: number,
  currency: DefaultCurrency,
): number {
  const pattern = detectPrecisionPattern(usdAmountInCents);
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency);

  let convertedAmount: number;
  if (isZeroDecimal) {
    convertedAmount = (usdAmountInCents / 100) * exchangeRate;
  } else {
    convertedAmount = usdAmountInCents * exchangeRate;
  }

  if (isZeroDecimal) {
    switch (pattern.precision) {
      case 'ten-thousand':
        return Math.round(convertedAmount / 100000) * 100000;

      case 'thousand':
        return Math.round(convertedAmount / 10000) * 10000;

      case 'hundred':
        return Math.round(convertedAmount / 1000) * 1000;

      case 'ten':
        return Math.round(convertedAmount / 100) * 100;

      case 'specific':
        return Math.round(convertedAmount);

      default:
        if (convertedAmount >= 1000) {
          return Math.round(convertedAmount / 10) * 10;
        }
        return Math.round(convertedAmount);
    }
  }

  const convertedMajor = convertedAmount / 100;

  switch (pattern.precision) {
    case 'specific':
      return Math.round(convertedAmount);

    case 'ten-thousand':
      return Math.round(convertedMajor / 1000) * 100000;

    case 'thousand':
      return Math.round(convertedMajor / 100) * 10000;

    case 'hundred':
      return Math.round(convertedMajor) * 100;

    case 'ten':
      if (pattern.magnitude === 'xlarge') {
        return Math.round(convertedMajor / 10) * 1000;
      }
      return Math.round(convertedMajor) * 100;

    case 'half-dollar': {
      const wholePart = Math.floor(convertedMajor);
      const fraction = convertedMajor - wholePart;
      if (fraction < 0.25) {
        return wholePart * 100;
      } else if (fraction < 0.75) {
        return wholePart * 100 + 50;
      } else {
        return (wholePart + 1) * 100;
      }
    }

    case 'dollar':
      if (pattern.hasMarketingEnding) {
        if (pattern.magnitude === 'small' || pattern.magnitude === 'medium') {
          const baseAmount = Math.floor(convertedMajor);
          if (pattern.marketingPattern === '.99') {
            return baseAmount * 100 + 99;
          } else if (pattern.marketingPattern === '.95') {
            return baseAmount * 100 + 95;
          } else if (pattern.marketingPattern === '.90') {
            return baseAmount * 100 + 90;
          }
        }
      }
      return Math.round(convertedMajor) * 100;

    default:
      return Math.round(convertedAmount);
  }
}

export interface CurrencyConversionEstimate {
  /**
   * The target currency code in lowercase (e.g., 'usd', 'eur', 'gbp')
   */
  currencyCode: DefaultCurrency;

  /**
   * Convert a USD amount (in cents) to the target currency
   * @param usdAmountInCents - Amount in USD cents (e.g., 99900 for $999.00)
   * @returns Converted amount in the target currency's minor units (or major units for zero-decimal currencies)
   */
  convert: (usdAmountInCents: number) => number;

  /**
   * Convert and format a currency amount using the current locale.  This is a convenience method that wraps
   * sail intl.formatCurrency method and applies the proper currency code conversion automatically.
   * @param amount - The amount to format
   * @param options - The options to use for formatting
   * @returns The formatted amount
   */
  convertAndFormatCurrency: (
    amount: number,
    options: Omit<FormatCurrencyOptions, 'currency'>,
  ) => string;
}

/**
 * Hook to get a currency converter for the current country
 * Returns a memoized converter function that can be called multiple times efficiently
 *
 * This is an ESTIMATE for demo/example purposes only, using approximate exchange rates
 *
 * @returns Object with currencyCode and convert function
 *
 * @example
 * const {currencyCode, convert} = useEstimatedCurrencyConversionForDemoPurposesOnly();
 * const amount1 = convert(99900); // Convert $999.00
 * const amount2 = convert(150000); // Convert $1,500.00
 * intl.formatCurrency(amount1, { currency: currencyCode.toUpperCase() });
 *
 * @example
 * // In US context
 * const {currencyCode, convert} = useEstimatedCurrencyConversionForDemoPurposesOnly();
 * // currencyCode: 'usd'
 * // convert(99900): 99900 (USD)
 *
 * @example
 * // In GB context
 * const {currencyCode, convert} = useEstimatedCurrencyConversionForDemoPurposesOnly();
 * // currencyCode: 'gbp'
 * // convert(99900): ~79920 (GBP, rounded to £799)
 *
 * @example
 * // In JP context
 * const {currencyCode, convert} = useEstimatedCurrencyConversionForDemoPurposesOnly();
 * // currencyCode: 'jpy'
 * // convert(99900): ~149850 (JPY, rounded to ¥150,000)
 */
export function useEstimatedCurrencyConversionForDemoPurposesOnly(
  overrideCountryCode?: CountryCode,
): CurrencyConversionEstimate {
  const contextCountryCode = useCountryCode();
  const countryCode = overrideCountryCode || contextCountryCode;
  const intl = useIntl();

  return useMemo(() => {
    const currencyCode = getCurrencyForCountry(countryCode);
    const exchangeRate = EXCHANGE_RATES[currencyCode] || 1.0;

    const convert = (usdAmountInCents: number): number => {
      if (exchangeRate === 1.0) {
        return usdAmountInCents;
      }

      return convertWithPrecisionIntent(
        usdAmountInCents,
        exchangeRate,
        currencyCode,
      );
    };

    const convertAndFormatCurrency = (
      amount: number,
      options: Omit<FormatCurrencyOptions, 'currency'>,
    ) => {
      return intl.formatCurrency(convert(amount), {
        currency: currencyCode,
        ...options,
      });
    };

    return {currencyCode, convert, convertAndFormatCurrency};
  }, [countryCode, intl]);
}
